import { AsyncPipe, CommonModule, CurrencyPipe, DatePipe, formatDate } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import {
    MAT_LEGACY_DIALOG_DATA, MatLegacyDialogConfig as MatDialogConfig,
    MatLegacyDialogModule,
    MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { DaveListCardModule } from 'src/app/dave-list-card/dave-list-card.module';
import {
    LedgerImportDocumentTypeNames,
    LedgerImportDocumentTypes,
    ledgerImportDocumentTypesValues,
    LedgerImportEntity,
    LedgerImportStatusNames,
} from '../../../dave-data-module/entities/ledger-import.entity';
import { State } from '../../../dave-data-module/State';
import { getCommissions } from '../../../dave-data-module/State/selectors/commission.selector';
import { getCustomers } from '../../../dave-data-module/State/selectors/customers.selectors';
import { getLedgerImports, getLedgerImportsFetched } from '../../../dave-data-module/State/selectors/ledger-import.selector';
import { getOwnPartnerOffices, getPartner } from '../../../dave-data-module/State/selectors/partners.selectors';
import { getUsers } from '../../../dave-data-module/State/selectors/users.selectors';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { AppFilterModule } from '../../../dave-utils-module/app-filter-module/app-filter.module';
import {
    FilterOption,
    FILTER_TYPE_SEARCH_MULTI_SELECT,
    IFilterTypeSearchMultiSelectValue,
    IFilterTypeTimespanValue, FILTER_TYPE_TIMESPAN,
} from '../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { BreakpointObserverService } from '../../../dave-utils-module/dave-shared-components-module/services/breakpoint-observer.service';
import { appMatDialogDefaultConfig, isNotNullOrUndefined } from '../../../helper/helper';
import { ReportsPageMeta } from '../../../helper/page-metadata';
import { CustomLabelService } from '../../../services/custom-label/custom-label.service';
import { FilterTypes } from '../../../services/default-filter.service';
import { LoadingService } from '../../../services/loading.service';
import { LedgerImportResolver } from '../../../dave-data-module/guards/ledger-import.resolver';
import { PartnerOfficeAdditionalData } from '../../../dave-data-module/entities/partnerOffice.entity';
import { HintIconComponent } from '../../../hint-icon/hint-icon.component';
import {
    MatLegacyPaginator,
    MatLegacyPaginatorIntl,
    MatLegacyPaginatorModule,
} from '@angular/material/legacy-paginator';
import { LedgerImportTableDataSource, LedgerImportTableItem, LiFilterValues } from './ledger-import-table-datasource';
import { DaveListCardComponent } from '../../../dave-list-card/dave-list-card.component';
import { GermanPaginatorIntl } from '../../../services/german-paginator-intl.service';
export interface SelectLedgerImportLargeDialogDialogData {
    defaultFilter?: Partial<LiFilterValues>;
}
export type SelectLedgerImportLargeDialogDialogReturnData = undefined | number;

@Component({
    selector: 'app-select-ledger-import-large-dialog',
    templateUrl: './select-ledger-import-large-dialog.component.html',
    styleUrls: ['./select-ledger-import-large-dialog.component.scss'],
    providers: [CurrencyPipe, DatePipe, { provide: MatLegacyPaginatorIntl, useClass: GermanPaginatorIntl },],
    imports: [
        CommonModule,
        AsyncPipe,
        DaveListCardModule,
        MatFormFieldModule,
        FontAwesomeModule,
        AppFilterModule,
        MatBadgeModule,
        ReactiveFormsModule,
        AppButtonModule,
        MatButtonModule,
        MatLegacyDialogModule,
        MatInputModule,
        MatSidenavModule,
        MatLegacyTooltipModule,
        HintIconComponent,
        MatLegacyPaginatorModule,
    ],
    standalone: true,
})
export class SelectLedgerImportLargeDialogComponent implements AfterViewInit {
    public static readonly DefaultConfig: MatDialogConfig = {
        ...appMatDialogDefaultConfig,
    }
    public Headers: Partial<Record<keyof LedgerImportTableItem, string>> = {
        CustomerNumber: 'KdNr.',
        CustomerName: 'Kundenname',
        Type: 'Typ',
        CreatedAt: 'Erstellungsdatum',
        DueDate: 'Fälligkeitsdatum',
        UpdatedAt: 'Änderungsdatum',
        InvoiceNumber: '',
        Status: 'Status',
        ConsolidatedAmount: 'Summe (Brutto)',
        ConsolidatedWithoutTax: 'Summe (Netto)',
        Notes: 'Notiz',
        Title: 'Titel',
        ConsolidatedDate: '',
    };

    @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;
    @ViewChild(MatLegacyPaginator) paginator!: MatLegacyPaginator;
    @ViewChild(DaveListCardComponent) listCard!: DaveListCardComponent;
    DisplayedColumns$: Observable<string[]>;
    protected searchVisible = false;

    protected searchForm = new FormControl<string>('');
    public LiFilterValues$: BehaviorSubject<LiFilterValues> = new BehaviorSubject({
        [FilterTypes.Customers]: [],
        [FilterTypes.Status]: [],
        [FilterTypes.Type]: [],
        [FilterTypes.Date]: {from: null, to: null,},
        [FilterTypes.AssignedUser]: [],
        [FilterTypes.AssignedCommission]: [],
    });
    public LiFilterSettings: Observable<FilterOption[]> = combineLatest([
        this.store.select(getUsers).pipe(filter(isNotNullOrUndefined)),
        this.store.select(getPartner).pipe(filter(isNotNullOrUndefined)),
        this.store.select(getCommissions).pipe(filter(isNotNullOrUndefined)),
        this.cls.getSingle$('Commission'),
        this.cls.getSingle$('CustomerSupplier'),
        this.store.select(getCustomers).pipe(filter(isNotNullOrUndefined)),
    ]).pipe(
        map(([users, partner, commissions, commissionLabel, customerLabel, customers]) => {
            let customerValues: IFilterTypeSearchMultiSelectValue<number>[] = customers.map((c) => ({
                id: c.Id,
                label: c.DisplayName,
            }));
            let statusValues: IFilterTypeSearchMultiSelectValue<string>[] = Array.from(LedgerImportStatusNames, ([key, val]) => ({
                id: key,
                label: val,
            }));
            let typeValues: IFilterTypeSearchMultiSelectValue<LedgerImportDocumentTypes>[] = ledgerImportDocumentTypesValues.map(e => ({
                id: e,
                label: LedgerImportDocumentTypeNames.get(e),
            }))
            let commissionValues: IFilterTypeSearchMultiSelectValue<number>[] = commissions.map((c) => ({
                id: c.Id,
                label: c.chipLabel,
            }));
            let userValues: IFilterTypeSearchMultiSelectValue<number>[] = users.map((u) => ({
                id: u.Id,
                label: u.DisplayName,
            }));

            const ret: FilterOption[] = [
                {
                    Name: FilterTypes.Customers,
                    Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                    Label: customerLabel,
                    Icon: 'filter',
                    Values: customerValues,
                },
                {
                    Name: FilterTypes.Status,
                    Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                    Label: 'Status',
                    Icon: 'check',
                    Values: statusValues,
                },
                {
                    Name: FilterTypes.Type,
                    Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                    Label: 'Typ',
                    Icon: 'filter',
                    Values: typeValues,
                },
                {
                    Name: FilterTypes.Date,
                    Type: FILTER_TYPE_TIMESPAN,
                    Label: 'Datum',
                    Icon: 'calendar',
                },
                {
                    Name: FilterTypes.AssignedUser,
                    Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                    Label: 'Bearbeiter',
                    Icon: 'filter',
                    Values: userValues,
                },
                {
                    Name: FilterTypes.AssignedCommission,
                    Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                    Label: commissionLabel,
                    Icon: 'filter',
                    Values: commissionValues,
                },
            ];
            return ret;
        }),
        startWith([]),
    );
    initialPageSize= 50;

    toggleSearch() {
        this.searchVisible = !this.searchVisible;
        this.searchForm.setValue('');
        if (this.searchVisible) {
            setTimeout(() => {
                this.searchInput.nativeElement.focus();
            }, 0);
        }
    }
    public FilterAmount$ = this.LiFilterValues$.pipe(
        map((filter) => {
            let amount = 0;
            if (filter[FilterTypes.Customers]?.length) {
                amount += 1;
            }
            if (filter[FilterTypes.Status]?.length) {
                amount += 1;
            }
            if (filter[FilterTypes.Type]?.length) {
                amount += 1;
            }
            if (filter[FilterTypes.AssignedUser]?.length) {
                amount += 1;
            }
            if (filter[FilterTypes.AssignedCommission]?.length) {
                amount += 1;
            }
            return amount;
        }),
    );



    onEntryClicked(entryId: number): void {
        this.dialogRef.close(entryId);
    }
    dataSource: LedgerImportTableDataSource;
    constructor(
        private currencyPipe: CurrencyPipe,
        @Inject(LOCALE_ID) private locale: string,
        protected cls: CustomLabelService,
        private store: Store<State>,
        bp: BreakpointObserverService,
        private datePipe: DatePipe,
        public LS: LoadingService,
        liRes: LedgerImportResolver,
        protected dialogRef: MatDialogRef<SelectLedgerImportLargeDialogComponent, SelectLedgerImportLargeDialogDialogReturnData>,
        @Inject(MAT_LEGACY_DIALOG_DATA) dialogData: SelectLedgerImportLargeDialogDialogData,
    ) {
        this.dataSource = new LedgerImportTableDataSource(store, currencyPipe, datePipe, locale);
        this.dataSource.filterValues$ = this.LiFilterValues$;
        this.dataSource.searchForm = this.searchForm;

        if (dialogData?.defaultFilter) {
            this.LiFilterValues$.next({...this.LiFilterValues$.value, ...dialogData.defaultFilter})
        }
        firstValueFrom(this.store.select(getLedgerImportsFetched)).then((fetched) => {
            if (!fetched) {
                liRes.resolve();
            }
        });
        this.DisplayedColumns$ = bp.MobileQuery.pipe(
            map((mobile) =>
                mobile
                    ? ['InvoiceNumber', 'Type', 'ConsolidatedDate']
                    : ['Title', 'Status','ConsolidatedDate', 'CreatedAt', 'Type', 'CustomerNumber', 'ConsolidatedWithoutTax', 'DueDate', 'UpdatedAt', 'InvoiceNumber', 'ConsolidatedAmount', 'CustomerName', 'Notes'],
            ),
        );
    }
    public Close() {
        this.dialogRef.close();
    }

    ngAfterViewInit() {
        this.dataSource.setPaginator(this.paginator);
        this.dataSource.setSort(this.listCard.sort);
    }

    protected readonly ReportsPageMeta = ReportsPageMeta;
}
